import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { ApplicationService } from "./application.service";
import { LeafletSendRequest } from "../interfaces/leaflet-send-request";
import { Article } from "../interfaces/article";
import { findIndex } from "underscore";
import * as _ from "underscore";
import { ApiResponse } from "../interfaces/api-response";
import { map } from 'rxjs/operators';
import { HelperService } from "./helper.service";
import { StorageTypes } from "../interfaces/helpers";
import { ArticleMatrix } from "../interfaces/article-matrix";
import { of } from "rxjs";
import { catchError } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class ArticleService {
  maxRecentlyViewed = 6;
  selectedArticleSpecCode: string;
  get recentlyViewedStorageKey() {
    return `rv-${this.applicationService.getSelectCompanyGroupCode()}`;
  }

  constructor(
    private http: HttpClient,
    private applicationService: ApplicationService,
    private helperService: HelperService
  ) {}

  // Get article bill of material
  public getArticleBom(article) {
    return this.http.get(
      environment.apiendpoint +
        "articles/" +
        article.single_article_code +
        "/bom/"
    );
  }
  // Get articles of the Bom group
  public getBomArticles(group, article) {
    return this.http.get(
      environment.apiendpoint +
        "articles/" +
        article.single_article_code +
        "/bom/" +
        group
    );
  }

  // Get set items
  public getSetItems(article) {
    return this.http.get(
      environment.apiendpoint +
        `articles/${
          article.single_article_code
            ? article.single_article_code
            : article.article_code
        }/setitems/`
    );
  }

  // Get multiple articles by article code
  public getArticleByCodes(articleCodes: String[]) {
    const requestData = this.applicationService.createQueryString(
      articleCodes,
      "c"
    );
    return this.http.get(environment.apiendpoint + "articles/" + requestData);
  }

  // Get leaflet
  public getLeaflet(articleId, articleCode) {
    const HTTPOptions = {
      headers: new HttpHeaders({
        Accept: "application/pdf",
      }),
      responseType: "blob" as "json",
    };

    return this.http.get(
      `${environment.apiendpoint}articles/leaflet/${articleId}/${
        articleCode ? articleCode : ""
      }?fileType=pdf`,
      HTTPOptions
    );
  }

  // Send leaflet to e-mail
  public sendLeaflet(requestInput: LeafletSendRequest) {
    return this.http.post(
      `${environment.apiendpoint}articles/sendleaflet/`,
      requestInput
    );
  }

  // Get batteries for a bicycle
  public getBatteriesAndChargers(articleCode) {
    return this.http
      .get(
        `${environment.apiendpoint}articles/${articleCode}/batteriesandchargers/`
      )
      .pipe(
        map((response: any) => {
          // Sort by retail price
          if (response && response.result["Batteries"]) {
            const batteries = response.result["Batteries"];
            let sortedBatteries = _.sortBy(
              batteries,
              function (battery: Article) {
                return battery.retail_price?.value;
              }
            );
            response.result["Batteries"] = sortedBatteries;
          }
          return response;
        })
      );
  }

  public getDownloadImage(modelId, imageNumber) {
    const HTTPOptions = {
      headers: new HttpHeaders({
        Accept: "application/jpeg",
      }),
      responseType: "blob" as "jpeg",
    };

    return this.http.get(
      `${environment.apiendpoint}articles/downloadimage/${modelId}/${imageNumber}?fileType=pdf`
    );
  }

  public getDownloadZip(modelId) {
    const HTTPOptions = {
      headers: new HttpHeaders({
        Accept: "application/octet-stream",
      }),
      responseType: "blob" as "zip",
    };

    return this.http.get(
      `${environment.apiendpoint}articles/downloadimages/${modelId}?fileType=pdf`
    );
  }

  public preorderArticleDetails(modelId: number) {
    return this.http.get(
      `${environment.apiendpoint}models/preorder/${modelId}`
    );
  }

  public updateRecentlyViewed(article: Article) {
    let recentList = (
      localStorage.getItem(this.recentlyViewedStorageKey)
        ? JSON.parse(localStorage.getItem(this.recentlyViewedStorageKey))
        : []
    ) as Article[];

    // Remove article from list if it's already in there
    const alreadyInListIndex = recentList.findIndex((a: Article) => {
      return a.id === article.id;
    });

    if (alreadyInListIndex >= 0) {
      recentList.splice(alreadyInListIndex, 1);
    }

    // Remove no 20 in the list
    if (recentList.length >= this.maxRecentlyViewed) {
      recentList.splice(this.maxRecentlyViewed - 1, 1);
    }

    // Add article
    recentList.unshift(article);

    // Save in storage
    localStorage.setItem(
      this.recentlyViewedStorageKey,
      JSON.stringify(recentList)
    );
  }

  public getRecentlyViewed() {
    return this.helperService.parseJsonFromStorage(this.recentlyViewedStorageKey, StorageTypes.localstorage)
  }

  // Get all the articles which are on the stock watch list
   // Get all the articles which are on the stock watch list
   public getArticleStockNotificationList(inputData) {

    return this.http.post(
      `${environment.apiendpoint}articles/stock/notifications/details`, inputData
    );
  }

  public getArticleStockNotifications() {
    return this.http.get(
      `${environment.apiendpoint}articles/stock/notifications`
    );
  }

  // Add article to stock notification watch list
  public addToStockNotificationList(
    articleCode: string,
    articleDescription: string
  ) {
    return this.http.post(
      `${environment.apiendpoint}articles/stock/notifications`,
      {
        article_code: articleCode,
        description: articleDescription,
      }
    );
  }

  // Remove article from stock notification watch list
  public removeFromStockNotificationList(articleCode: string) {
    return this.http.delete(
      `${environment.apiendpoint}articles/stock/notifications/${articleCode}`
    );
  }

  // Get multiple articles by article code
  public getStockNotificationArticleData(
    articleCode: string,
    companyGroupCode: string
  ) {
    return this.http.get(
      `${environment.apiendpoint}articles/${articleCode}?companyGroupCode=${companyGroupCode}`
    );
  }

  public getVolumePrice(volumePrice) {
    for (const key in volumePrice) {
      if (volumePrice.hasOwnProperty(key)) {
        const price = volumePrice[key];
        for (const subKey in price) {
          if (price.hasOwnProperty(subKey)) {
            const languageItem = price[subKey];
            for (const lang in languageItem) {
              if (languageItem.hasOwnProperty(lang)) {
                return languageItem[lang];
              }
            }
          }
        }
      }
    }
  }

  getTempUndeliverable(matrix: ArticleMatrix, singleArticleCode: string) {
    let modelTemporarilyUndeliverable = false;
    if (matrix?.options && matrix?.options?.length) {
      let tempCount = 0;
      for (let i = 0; i < matrix.options.length; i++) {
        const articles = matrix.options[i].value as Article[];
        articles.forEach((a: Article) => {
          if (
            a.temporarily_undeliverable &&
            a.single_article_code &&
            a.single_article_code === singleArticleCode
          ) {
            modelTemporarilyUndeliverable = true;
          }

          if (a.temporarily_undeliverable) {
            tempCount++;
          }
        });
      }

      // If all articles are temporarily_undeliverable then always show not available
      if (tempCount === matrix?.options?.length) {
        modelTemporarilyUndeliverable = true;
      }

      return modelTemporarilyUndeliverable;
    }
  }

  // Get recommendations for the current dealer
  public getRecommendations(type: 'TOP' | 'POPULAR' | 'RECOMMENDED' = 'RECOMMENDED') {
    return this.http.get(`${environment.apiendpoint}articles/recommendations?type=${type}`);
  }

  private normalizeDescriptionValue(value: string): string {
    return (value || "").trim().replace(/\s+/g, " ");
  }

  private createSlug(value: string): string {
    return this.normalizeDescriptionValue(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  private toDisplayColor(colorName: string): string {
    const normalizedColor = this.normalizeDescriptionValue(colorName).toLowerCase();

    if (!normalizedColor) {
      return "#9ca3af";
    }

    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(normalizedColor)) {
      return normalizedColor;
    }

    if (/^(rgb|rgba|hsl|hsla)\(/i.test(normalizedColor)) {
      return normalizedColor;
    }

    const knownColors: { [key: string]: string } = {
      black: "#111827",
      white: "#ffffff",
      grey: "#6b7280",
      gray: "#6b7280",
      silver: "#9ca3af",
      red: "#dc2626",
      blue: "#2563eb",
      green: "#16a34a",
      yellow: "#eab308",
      orange: "#f97316",
      purple: "#7c3aed",
      pink: "#ec4899",
      brown: "#92400e",
      beige: "#d6d3c9",
      gold: "#d4af37",
      bronze: "#b45309",
      titanium: "#9ca3af",
      anthracite: "#4b5563",
      turquoise: "#14b8a6",
      teal: "#0f766e",
      navy: "#1e3a8a",
      matte: "#6b7280",
      matt: "#6b7280",
    };

    const colorTokens = normalizedColor.split(/[^a-z0-9]+/).filter(Boolean);
    for (const token of colorTokens) {
      if (knownColors[token]) {
        return knownColors[token];
      }
    }

    let hash = 0;
    for (let i = 0; i < normalizedColor.length; i++) {
      hash = normalizedColor.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 65%, 50%)`;
  }

  public getLineupProducts(brand: string, modelYear?: string) {
    const encodedBrand = encodeURIComponent(brand);
    const normalizedModelYear = String(modelYear || '').trim();
    const modelYearQuery = normalizedModelYear ? `?modelyear=${encodeURIComponent(normalizedModelYear)}` : '';

    return this.http.get(`${environment.apiendpoint}pim/tools/modelfamilies/brand/${encodedBrand}${modelYearQuery}`).pipe(
      map((response: any) => {
        const apiData = response?.result || response;
        const { model_family_names, ...families } = apiData;
        const result: { [familyName: string]: any[] } = {};
        const modelYears = this.normalizeModelYearCounts(
          apiData?.model_years || apiData?.model_year_counts || apiData?.years || {}
        );
  

        for (const familyName of Object.keys(families.model_families || {})) {
          const models = families.model_families[familyName];
          if (!Array.isArray(models)) {
            continue;
          }
          // Group models by name and merge color variants
          const grouped: {
            [name: string]: {
              id: number;
              uiKey: string;
              brand: string;
              name: string;
              thumbnail: string;
              urlCode: string;
              colorMap: Map<string, { modelId: number; thumbnail: string }>;
              years: Set<string>;
            }
          } = {};
          for (const model of models) {
            if (Number(model.model_year) === Number(modelYear) || modelYear === '') {
              const name = (model.model_name || "").trim();
              const modelThumbnail = model.articles?.[0]?.images?.[0] || "";
              if (!grouped[name]) {
                grouped[name] = {
                  id: model.model_id,
                  uiKey: this.createSlug(`${familyName}-${model.model_id}`),
                  brand: brand.toUpperCase(),
                  name,
                  thumbnail: modelThumbnail,
                  urlCode: this.createSlug(`${name}-${model.model_id}`),
                  colorMap: new Map<string, { modelId: number; thumbnail: string }>(),
                  years: new Set<string>(),
                };
              } else if (!grouped[name].thumbnail) {
                grouped[name].thumbnail = modelThumbnail;
              }

              for (const year of this.extractModelYears(model)) {
                grouped[name].years.add(year);
              }

              for (const a of (model.articles || [])) {
                const colorName = (a.main_colour || "").trim();
                if (colorName && !grouped[name].colorMap.has(colorName)) {
                  grouped[name].colorMap.set(colorName, {
                    modelId: model.model_id,
                    thumbnail: a.images?.[0] || modelThumbnail,
                  });
                }
              }
            }
          }
          result[familyName] = Object.values(grouped).map(({ colorMap, years, ...rest }) => ({
            ...rest,
            years: Array.from(years).sort((a, b) => Number(b) - Number(a)),
            variants: Array.from(colorMap.entries()).map(([colorName, data]) => ({
              name: colorName,
              color: this.toDisplayColor(colorName),
              modelId: data.modelId,
              thumbnail: data.thumbnail,
            })),
          }));
        }

        return { result, modelYears, success: true };
      }),
      catchError(() => of({ result: {}, modelYears: {}, success: false }))
    );
  }

  private normalizeModelYearCounts(raw: any): { [year: string]: number } {
    if (!raw || typeof raw !== 'object') {
      return {};
    }

    const normalized: { [year: string]: number } = {};
    for (const [yearKey, count] of Object.entries(raw)) {
      const year = this.normalizeYearValue(yearKey);
      const parsedCount = Number(count);
      if (year && Number.isFinite(parsedCount) && parsedCount > 0) {
        normalized[year] = parsedCount;
      }
    }

    return normalized;
  }

  private extractModelYears(model: any): string[] {
    const candidates: any[] = [
      model?.model_year,
      model?.year,
      model?.modelYear,
      model?.collection_year,
      ...(Array.isArray(model?.model_years) ? model.model_years : []),
    ];

    if (Array.isArray(model?.articles)) {
      for (const article of model.articles) {
        candidates.push(article?.model_year, article?.year, article?.collection_year);
      }
    }

    return Array.from(
      new Set(
        candidates
          .map((value) => this.normalizeYearValue(value))
          .filter((value): value is string => !!value)
      )
    );
  }

  private normalizeYearValue(value: any): string | null {
    const year = String(value ?? '').trim();
    return /^\d{4}$/.test(year) ? year : null;
  }

  public downloadLineupProducts(brand: string, modelYear?: string) {
    const encodedBrand = encodeURIComponent(brand);
    const normalizedModelYear = String(modelYear || '').trim();
    const modelYearQuery = normalizedModelYear ? `?modelyear=${encodeURIComponent(normalizedModelYear)}` : '';

    const HTTPOptions = {
      headers: new HttpHeaders({ Accept: 'application/octet-stream' }),
      responseType: 'blob' as 'json',
    };

    return this.http.get(
      `${environment.apiendpoint}pim/tools/modelfamilies/brand/${encodedBrand}/download${modelYearQuery}&t=excel`,
      HTTPOptions
    );
  }

}

